import React, { createContext, useState, useEffect, useRef } from 'react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { dashboardService } from '../services/dashboardService';
export const DashboardContext = createContext();

export const DashboardProvider = ({ children }) => {

  const [dateRange, setDateRange] = useState({
    startDate: startOfDay(subDays(new Date(), 6)),
    endDate: endOfDay(new Date()),
    preset: 'last7days',
  });

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    todayRevenue: 0,
    todayOrders: 0,
    activeTables: 0,
    totalTables: 0,
    paymentSplit: {},
    revenueHistory: [],
    topSellingItems: [],
    topDish: { name: "N/A", quantity: 0, change: 0 },
    bankCollections: [],
    financialSnapshot: { totalRevenue: 0, totalDiscount: 0, totalNcAmount: 0, totalTax: 0, grossSales: 0 },
    loading: true
  });

  //  API calls
  const isFetching = useRef(false);


  //  MAP DATE RANGE TO BACKEND PARAM

const getPeriodParam = (preset) => {
  switch (preset) {
    case 'today': return 'TODAY';
    case 'yesterday': return 'YESTERDAY';
    case 'last7days': return '7D';
    case 'last15days': return '15D';
    case 'last30days': return '1M';
    case 'last3months': return '3M';
    case 'last6months': return '6M';
    case 'last1year': return '1Y';
    default: return '7D';
  }
};

const rangeLabelMap = {
  today: "Today",
  yesterday: "Yesterday",
  last7days: "Last 7 Days",
  last30days: "Last 30 Days",
  custom: "Custom Range",
};




const fetchDashboardData = async () => {
  if (isFetching.current) return;

  try {
    isFetching.current = true;
    setDashboardData((prev) => ({ ...prev, loading: true }));

    
      let queryParam;

      if (dateRange.preset === "custom") {
        queryParam = {
          startDate: format(dateRange.startDate, "yyyy-MM-dd"),
          endDate: format(dateRange.endDate, "yyyy-MM-dd"),
        };
      } else {
        queryParam = {
          period: getPeriodParam(dateRange.preset),
        };
      }

      if (selectedBranchId) {
        queryParam.branchId = selectedBranchId;
      }

    const [
      topSellingResponse,
      topDishResponse,
      paymentCollectionResponse,
      revenueTrendResponse,
      paymentSplitResponse,
      revenueSummaryResponse,
      ordersSummaryResponse,
      financialSnapshotResponse
    ] = await Promise.all([
      dashboardService.getTopSellingItems(queryParam),
      dashboardService.getTopDish(queryParam),
      dashboardService.getPaymentCollectionModeWise(queryParam),
      dashboardService.getRevenueTrend(queryParam),
      dashboardService.getPaymentSplit(queryParam),
      dashboardService.getRevenueSummary(queryParam),
      dashboardService.getOrdersSummary(queryParam),
      dashboardService.getFinancialSnapshot(queryParam),
    ]);

    // ---------- FORMAT TOP SELLING ----------
const formattedTopSelling = Array.isArray(topSellingResponse)
  ? topSellingResponse.map(i => ({
      name: i.itemName ?? "Unknown Item",
      quantity: i.todayQuantity ?? 0,

     
      unitPrice: i.unitPrice ?? 0,
      price: i.unitPrice ?? 0,                 
      amount: i.todayAmount ?? 0,
      totalSalesAmount: i.totalSalesAmount ?? 0,

      category: i.trend ?? "General",          
      trend: i.trend ?? "Stable",

      salesChangePercentage: i.salesChangePercentage ?? 0,
    }))
  : [];


    // ---------- FORMAT TOP DISH (single object API) ----------
const formattedTopDish = topDishResponse
  ? {
      name: topDishResponse.itemName ?? "Unknown Dish",
      quantity: topDishResponse.quantity ?? 0,
      change: topDishResponse.growthPercentage ?? 0,
    }
  : { name: "N/A", quantity: 0, change: 0 };



    // ---------- FORMAT REVENUE TREND ----------
    const formattedRevenueTrend = Array.isArray(revenueTrendResponse)
      ? revenueTrendResponse.map(i => ({
          date: i.date,
          revenue: i.totalRevenue ?? 0,
          orders: i.totalOrders ?? 0, 
          averageRevenue: i.averageRevenue ?? 0
        }))
      : [];

    // ---------- PAYMENT SPLIT ----------
    const formattedPaymentSplit = {
      cash: paymentSplitResponse?.cashPercent ?? 0,
      online: paymentSplitResponse?.onlinePercent ?? 0,
    };

    // ---------- SUMMARY FIELDS FOR UI CARDS ----------
    const todayRevenue = revenueSummaryResponse?.currentRevenue ?? 0;
    const todayOrders = ordersSummaryResponse?.currentOrders ?? 0;

    // ---------- UPDATE STATE ----------

    setDashboardData((prev) => ({
      ...prev,

      todayRevenue,
      todayOrders,
      yesterdayRevenue: revenueSummaryResponse?.previousRevenue ?? 0,
      revenueChange: revenueSummaryResponse?.growthPercentage ?? 0,
      yesterdayOrders: ordersSummaryResponse?.previousOrders ?? 0,
      ordersChange: ordersSummaryResponse?.growthPercentage ?? 0,


      revenueSummary: revenueSummaryResponse,
      ordersSummary: ordersSummaryResponse,

      topSellingItems: formattedTopSelling,
     topDish: formattedTopDish,


      paymentSplit: formattedPaymentSplit,
      bankCollections: Array.isArray(paymentCollectionResponse)
          ? paymentCollectionResponse              
         : [],
      revenueHistory: formattedRevenueTrend,
      financialSnapshot: {
        totalRevenue: Number(financialSnapshotResponse?.totalRevenue ?? 0),
        totalDiscount: Number(financialSnapshotResponse?.totalDiscount ?? 0),
        totalNcAmount: Number(financialSnapshotResponse?.totalNcAmount ?? 0),
        totalTax: Number(financialSnapshotResponse?.totalTax ?? 0),
        grossSales: Number(financialSnapshotResponse?.grossSales ?? 0),
      },
      selectedRange: dateRange.preset,
      rangeLabel: rangeLabelMap[dateRange.preset],

      loading: false,
    }));

  } catch (err) {
    console.error(" Error fetching dashboard data:", err);
    setDashboardData((prev) => ({ ...prev, loading: false }));
  } finally {
    isFetching.current = false;
  }
};

  // UPDATE DATE RANGE
 
  const updateDateRange = (preset) => {
    const today = new Date();
    let newRange = {};

    switch (preset) {
      case 'today':
        newRange = { startDate: startOfDay(today), endDate: endOfDay(today), preset: 'today' };
        break;
      case 'yesterday':
        newRange = {
          startDate: startOfDay(subDays(today, 1)),
          endDate: endOfDay(subDays(today, 1)),
          preset: 'yesterday',
        };
        break;
      case 'last7days':
        newRange = {
          startDate: startOfDay(subDays(today, 6)),
          endDate: endOfDay(today),
          preset: 'last7days',
        };
        break;
      case 'last30days':
        newRange = {
          startDate: startOfDay(subDays(today, 29)),
          endDate: endOfDay(today),
          preset: 'last30days',
        };
        break;
      default:
        return;
    }

    setDateRange(newRange);
  };

  const setCustomDateRange = (startDate, endDate) => {
    // Convert string dates from input fields to Date objects
    const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
    const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

    setDateRange({
      startDate: startOfDay(start),
      endDate: endOfDay(end),
      preset: 'custom',
    });
  };

 
  useEffect(() => {
    fetchDashboardData();

  }, [dateRange, selectedBranchId]);


  const value = {
    dashboardData,
    dateRange,
    updateDateRange,
    setCustomDateRange,
    refreshData: fetchDashboardData,
    selectedBranchId,
    setSelectedBranchId,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};